import { useState } from "react";
import type { Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { useI18n } from "../../i18n/index.js";
import { EndScreen, type RoundSummary } from "../../components/game/EndScreen.js";
import { HuessworkStart, type HuessworkOptions } from "./HuessworkStart.js";
import { HuessworkPlay } from "./HuessworkPlay.js";
import { fetchHuessworkConfig, type HuessworkConfig } from "./api.js";
import styles from "./Huesswork.module.css";

type Phase =
  | { kind: "start" }
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "playing"; config: HuessworkConfig; difficulty: Difficulty }
  | { kind: "finished"; rounds: RoundSummary[]; totalScore: number; maxScore: number; difficulty: Difficulty };

export function Huesswork() {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ kind: "start" });
  const [lastOptions, setLastOptions] = useState<HuessworkOptions | null>(null);

  async function startGame(options: HuessworkOptions) {
    setLastOptions(options);
    setPhase({ kind: "loading" });
    try {
      const config = await fetchHuessworkConfig();
      setPhase({ kind: "playing", config, difficulty: options.difficulty });
    } catch {
      setPhase({ kind: "error" });
    }
  }

  function handleFinish(roundSummaries: RoundSummary[], totalScore: number, difficulty: Difficulty) {
    const maxScore = roundSummaries.reduce((sum, r) => sum + r.maxPoints, 0);
    setPhase({ kind: "finished", rounds: roundSummaries, totalScore, maxScore, difficulty });
  }

  function handleReplay() {
    if (lastOptions) void startGame(lastOptions);
  }

  if (phase.kind === "start") {
    return <HuessworkStart onStart={startGame} />;
  }

  if (phase.kind === "loading") {
    return (
      <main className={styles.statusMain}>
        <p>{t("common.loading")}</p>
      </main>
    );
  }

  if (phase.kind === "error") {
    return (
      <main className={styles.statusMain}>
        <p className={styles.error}>{t("huesswork.play.gameLoadError")}</p>
        <button type="button" className={styles.retryButton} onClick={() => setPhase({ kind: "start" })}>
          {t("timeslip.start.startButton")}
        </button>
      </main>
    );
  }

  if (phase.kind === "playing") {
    return (
      <HuessworkPlay
        config={phase.config}
        difficulty={phase.difficulty}
        onFinish={(rounds, totalScore) => handleFinish(rounds, totalScore, phase.difficulty)}
      />
    );
  }

  return (
    <EndScreen
      gameName={GAME_BRANDING.huesswork.name}
      difficulty={phase.difficulty}
      totalScore={phase.totalScore}
      maxScore={phase.maxScore}
      rounds={phase.rounds}
      onReplay={handleReplay}
    />
  );
}
